// Clasificación del canal de adquisición: ¿de dónde vino esta sesión?
//
// La pregunta que responde: de todo el tráfico, cuánto lo trae la
// publicidad paga, cuánto es orgánico y cuánto entra directo.
//
// Advertencia central de diseño: **un ad de Instagram y un click
// orgánico desde Instagram llegan idénticos**. Mismo navegador in-app,
// mismo referrer (o ninguno — el WebView de Meta suele borrarlo). No hay
// nada del lado del sitio que los distinga. La ÚNICA forma confiable de
// separarlos es etiquetar el link del anuncio con `utm_medium=paid`.
// Sin esa etiqueta, un ad cae en `organic_social` o en `direct`.
//
// `fbclid` no sirve para separar pago de orgánico: Meta lo agrega en
// clicks salientes en general, incluidos los de posts orgánicos. Solo
// indica "vino de Meta", y lo usamos únicamente como desempate cuando
// no hay referrer ni UTM.

export type TrafficChannel =
  | "ads"
  | "ai_assistant"
  | "organic_social"
  | "organic_search"
  | "referral"
  | "direct";

export type AttributionInput = {
  /** `document.referrer` crudo. */
  referrer?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  /** Query string completo del landing, para detectar fbclid/igshid. */
  queryString?: string | null;
  /** Host propio, para no contar navegación interna como referral. */
  selfHost?: string | null;
};

const MEDIUM_PAGO = new Set([
  "paid", "cpc", "ppc", "paid_social", "paidsocial", "paid-social", "ads", "ad",
]);

const MEDIUM_SOCIAL_ORGANICO = new Set([
  "bio", "organic", "social", "organic_social", "post", "story", "stories",
]);

const HOSTS_SOCIAL = [
  "instagram.com", "l.instagram.com",
  "facebook.com", "l.facebook.com", "m.facebook.com", "lm.facebook.com",
  "t.co", "twitter.com", "x.com",
  "tiktok.com", "linkedin.com", "pinterest.com",
  "whatsapp.com", "wa.me", "youtube.com", "threads.net",
];

const HOSTS_BUSCADOR = [
  "google.", "bing.com", "duckduckgo.com", "yahoo.", "ecosia.org",
  "brave.com", "yandex.", "baidu.com",
];

const SOURCES_SOCIAL = new Set([
  "instagram", "ig", "facebook", "fb", "meta", "tiktok", "twitter", "x",
  "linkedin", "whatsapp", "youtube", "threads",
]);

/**
 * Asistentes de IA. Mismo patrón para `utm_source` y para el host del
 * referrer, porque llegan de las dos formas.
 *
 * Medido el 2026-09-12 sobre producción: de los 8 visitantes históricos
 * desde ChatGPT, **7 traían `utm_source=chatgpt.com`** con `utm_medium`
 * vacío. Como el medium viene null, la regla de social no los agarra y
 * caían en `referral`, mezclados con cualquier otro sitio.
 *
 * Merece canal propio y no un subconjunto de `referral` porque se
 * comporta distinto: es el canal con la tasa de click a WhatsApp más
 * alta que tenemos, y es el que hay que vigilar para saber si el
 * descubrimiento por asistentes crece o es anécdota.
 */
// Lista de dominios exactos y anclada al final, no una búsqueda de
// palabras sueltas: un patrón como /openai\b/ matchea
// `openai-fanclub.com.ar`, que no es un asistente. Los subdominios
// entran por el `(^|\.)` inicial.
const DOMINIOS_ASISTENTE = [
  "chatgpt.com",
  "openai.com",
  "perplexity.ai",
  "claude.ai",
  "gemini.google.com",
  "copilot.microsoft.com",
  "you.com",
  "phind.com",
];

const PATRON_ASISTENTE = new RegExp(
  `(^|\\.)(${DOMINIOS_ASISTENTE.map((d) => d.replace(/\./g, "\\.")).join("|")})$`,
);

function hostDe(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

function norm(v: string | null | undefined): string {
  return (v ?? "").trim().toLowerCase();
}

/**
 * Clasifica el canal según las reglas del plan, en orden — la primera
 * que matchea gana. La entrada cruda (referrer + UTMs) se persiste
 * aparte, así que si esta clasificación resulta mal calibrada se puede
 * recalcular sobre lo ya recolectado en vez de perderlo.
 */
export function classifyChannel(input: AttributionInput): TrafficChannel {
  const medium = norm(input.utmMedium);
  const source = norm(input.utmSource);

  // 1. Medium pago explícito — la única señal confiable de publicidad.
  if (medium && MEDIUM_PAGO.has(medium)) return "ads";

  // 1b. Auto-etiquetado de Google Ads: gclid (Search/Display), gbraid y
  //     wbraid (variantes iOS). Solo los agrega un click de anuncio —
  //     un resultado orgánico jamás los trae. Sin esta regla, un click
  //     de Search Ads sin UTMs manuales caía en organic_search y
  //     contaminaba la métrica clave del funnel.
  const qs = norm(input.queryString);
  if (/(?:^|[?&])(gclid|gbraid|wbraid)=/.test(qs)) return "ads";

  // 1c. Asistentes de IA. Va ANTES que la clasificación por referrer:
  //     ChatGPT se auto-etiqueta con `utm_source=chatgpt.com` y
  //     `utm_medium` vacío, así que sin esta regla caía en `referral`
  //     junto con cualquier otro sitio que nos linkee.
  const hostRef = hostDe(input.referrer);
  if (
    (source && PATRON_ASISTENTE.test(source.replace(/^www\./, ""))) ||
    (hostRef && PATRON_ASISTENTE.test(hostRef))
  ) {
    return "ai_assistant";
  }

  // 2. Etiquetado explícito como orgánico, o source de red social sin
  //    medium pago (ej. `utm_source=instagram` a secas).
  if (medium && MEDIUM_SOCIAL_ORGANICO.has(medium)) return "organic_social";
  if (source && SOURCES_SOCIAL.has(source)) return "organic_social";

  // 3/4/5. Por referrer, ignorando navegación interna.
  const host = hostRef;
  const self = norm(input.selfHost).replace(/^www\./, "");
  if (host && !(self && (host === self || host.endsWith(`.${self}`)))) {
    if (HOSTS_SOCIAL.some((h) => host === h || host.endsWith(`.${h}`))) {
      return "organic_social";
    }
    if (HOSTS_BUSCADOR.some((h) => host.startsWith(h) || host.includes(h))) {
      return "organic_search";
    }
    return "referral";
  }

  // Sin referrer ni UTM: si Meta dejó su parámetro, al menos sabemos que
  // vino de Meta — pero NO si fue pago, así que va a orgánico social.
  if (qs.includes("fbclid=") || qs.includes("igshid=")) return "organic_social";

  // 6. Directo (o el referrer se perdió).
  return "direct";
}

/** Etiqueta legible para el dashboard. */
export const CHANNEL_LABEL: Record<TrafficChannel, string> = {
  ads: "Publicidad",
  ai_assistant: "Asistentes de IA",
  organic_social: "Redes (orgánico)",
  organic_search: "Buscadores",
  referral: "Otros sitios",
  direct: "Directo",
};

/** Extrae los UTM de un query string. */
export function parseUtms(queryString: string | null | undefined): {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
} {
  const p = new URLSearchParams(queryString ?? "");
  const get = (k: string) => {
    const v = p.get(k);
    return v && v.trim() ? v.trim().slice(0, 100) : null;
  };
  return {
    utmSource: get("utm_source"),
    utmMedium: get("utm_medium"),
    utmCampaign: get("utm_campaign"),
  };
}
